import { useState, useEffect, useCallback, useRef } from 'react'
import { 
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts'
import { getCumulativeSalesData } from '../../supabase-client'
import DateRangeSelector from './DateRangeSelector'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, entry) => sum + entry.value, 0)
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg max-w-xs backdrop-blur-sm">
        <p className="text-sm font-medium mb-2 text-gray-900">{label}</p>
        <div className="space-y-1">
          {[...payload]
            .filter(entry => entry.value > 0) // Only show customers with sales
            .sort((a, b) => b.value - a.value) // Biggest first
            .slice(0, 8) // Show top 8
            .map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm flex-shrink-0" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-600 truncate">
                  {entry.name}
                </span>
              </div>
              <span className="font-medium text-gray-900">
                £{entry.value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
              </span>
            </div>
          ))}
          {payload.filter(entry => entry.value > 0).length > 8 && (
            <div className="text-xs text-gray-500 pt-1 border-t border-gray-200">
              +{payload.filter(entry => entry.value > 0).length - 8} more customers
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 pt-2 mt-2">
          <div className="flex justify-between text-sm font-semibold text-gray-900">
            <span>Total:</span>
            <span>£{total.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export default function CumulativeSalesChart({ dateRange: externalDateRange, loading: externalLoading, compact = false }) {
  const [internalDateRange, setInternalDateRange] = useState('ytd')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const customerColorMap = useRef({})

  // Use external date range if provided, otherwise use internal
  const dateRange = externalDateRange || internalDateRange

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const cumulativeData = await getCumulativeSalesData(dateRange)
      setData(cumulativeData)
    } catch (error) {
      console.error('Error loading cumulative sales data:', error)
      setData([])
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    // Cumulative sales needs detailed customer data, so always use individual fetching for now
    // TODO: Could be optimized in future by processing shared raw sales data
    loadData()
  }, [loadData])

  const isLoading = externalLoading !== undefined ? externalLoading : loading
  if (isLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          Loading cumulative sales data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector 
              value={dateRange} 
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-no-data">
          No cumulative sales data available for the selected period
        </div>
      </div>
    )
  }

  // Color palette matching bar charts — primary brand colours first, then muted variants
  const colors = [
    '#009689', // Teal (primary)
    '#104E64', // Dark teal
    '#F54A00', // Orange-red
    '#FFB900', // Yellow
    '#FE9A00', // Orange
    '#6366F1', // Indigo
    '#3366FF', // Blue
    '#16A34A', // Green
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#0891B2', // Cyan
    '#D97706', // Amber
    '#059669', // Emerald
    '#7C3AED', // Violet
    '#DC2626', // Red
    '#2563EB', // Royal blue
    '#CA8A04', // Dark yellow
    '#0D9488', // Teal light
    '#9333EA', // Deep purple
    '#E11D48', // Rose
  ]

  // Get all unique customer names from the data
  const allCustomers = new Set()
  data.forEach(period => {
    Object.keys(period).forEach(key => {
      if (key !== 'month' && key !== 'total') {
        allCustomers.add(key)
      }
    })
  })

  // Sort customers by their final cumulative total — largest at bottom of stack (rendered first)
  const lastPeriod = data[data.length - 1] || {}
  const customerArray = Array.from(allCustomers).sort((a, b) =>
    (lastPeriod[b] || 0) - (lastPeriod[a] || 0)
  )

  // Assign stable colours by rank on first load, then keep them across date range switches
  if (Object.keys(customerColorMap.current).length === 0) {
    customerArray.forEach((customer, index) => {
      customerColorMap.current[customer] = index
    })
  } else {
    // Assign new customers the next available colour index
    customerArray.forEach(customer => {
      if (!(customer in customerColorMap.current)) {
        customerColorMap.current[customer] = Object.keys(customerColorMap.current).length
      }
    })
  }

  return (
    <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
      {!compact && !externalDateRange && (
        <div className="flex justify-between items-center mb-4">
          <div></div>
          <DateRangeSelector 
            value={dateRange} 
            onValueChange={setInternalDateRange}
            className="w-[140px]"
          />
        </div>
      )}
      <ResponsiveContainer key={customerArray.join(',')} width="100%" height={compact ? 300 : 400}>
        <AreaChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="month"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tick={{ fontSize: compact ? 10 : 12, angle: -45, textAnchor: 'end' }}
            interval={compact ? 'preserveStartEnd' : 0}
            height={60}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tick={{ fontSize: compact ? 10 : 12 }}
            tickFormatter={(value) => `£${(value / 1000).toFixed(1)}k`}
          />
          <Tooltip content={<CustomTooltip />} position={{ x: 100, y: 10 }} />
          
          {/* Render areas for each customer */}
          {customerArray.map((customer) => {
            const colorIndex = customerColorMap.current[customer] % colors.length
            return (
              <Area
                key={customer}
                type="monotone"
                dataKey={customer}
                stackId="1"
                stroke={colors[colorIndex]}
                fill={colors[colorIndex]}
                fillOpacity={0.8}
                strokeWidth={1}
              />
            )
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}